import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  /**
   * The layout currently applied to the grid.
   * @type {string}
   */
  #currentLayout = 'default';

  /**
   * The layout requested while a view transition is mid-flight. When a
   * transition is running we don't start a second one — we remember the most
   * recent request here and apply it once the running transition settles
   * ("latest wins", so rapid clicks never queue up or desync from the radios).
   * @type {string | null}
   */
  #pendingLayout = null;

  /**
   * Whether a layout view transition is currently running.
   * @type {boolean}
   */
  #isAnimating = false;

  connectedCallback() {
    super.connectedCallback();

    const { grid } = this.refs;
    if (grid) {
      this.#currentLayout = grid.getAttribute('product-grid-view') ?? 'default';
    }

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#requestLayout(target.value);
  }

  /**
   * Toggles the second-image view: every product card in the grid switches to
   * its second image (and back).
   *
   * @param {Event} event
   */
  toggleSecondImageView({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    const enabled = target.checked;

    // The toggle renders once per toolbar variant (desktop + mobile bars), so
    // keep every copy in sync with the one that was clicked.
    for (const input of this.querySelectorAll('input[name="second-image-view"]')) {
      if (input instanceof HTMLInputElement) input.checked = enabled;
    }

    this.#setSecondImageView(enabled);
  }

  /**
   * Applies the second-image view and persists it for the session. The view
   * itself is pure CSS keyed off the grid's [product-grid-second-image]
   * attribute (see card-gallery.liquid), so filtered, sorted, and
   * infinite-scrolled cards render it with no extra work — the attribute is
   * preserved across section morphs (see morph.js) and restored from
   * sessionStorage on load (see product-grid.liquid).
   *
   * @param {boolean} enabled
   */
  #setSecondImageView(enabled) {
    const { grid } = this.refs;
    if (!grid) return;

    startViewTransition(() => {
      // The attribute carries a value (not a bare boolean attribute) because
      // the morph in morph.js only preserves attributes with truthy values
      // across facet re-renders.
      if (enabled) {
        grid.setAttribute('product-grid-second-image', 'true');
      } else {
        grid.removeAttribute('product-grid-second-image');
      }

      // Park every gallery after the reorder: snap-target preservation and
      // leftover hover/variant scroll positions would otherwise surface an
      // arbitrary image.
      for (const card of grid.querySelectorAll('product-card')) {
        const productCard = /** @type {import('@theme/product-card').ProductCard} */ (card);
        productCard.resetGalleryForSecondImageView?.(enabled);
      }
    }, ['product-grid']);

    requestIdleCallback(() => {
      try {
        if (enabled) {
          sessionStorage.setItem('product-grid-second-image', 'true');
        } else {
          sessionStorage.removeItem('product-grid-second-image');
        }
      } catch {
        // Ignore storage failures (private mode, quota) — the view still applies.
      }
    });
  }

  /**
   * Requests a layout change. No-ops when the value is already applied, and
   * coalesces requests that arrive while a transition is running.
   *
   * @param {string} value
   */
  #requestLayout(value) {
    // Already showing this layout (and nothing newer is queued) — nothing to do.
    if (value === this.#currentLayout && this.#pendingLayout === null) return;

    if (this.#isAnimating) {
      // A transition is in flight: just record the latest intent.
      this.#pendingLayout = value;
      return;
    }

    this.#animateLayoutChange(value);
  }

  /**
   * Animates the layout change, then drains any layout requested mid-transition.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;
    if (!grid) return;

    this.#isAnimating = true;
    this.#pendingLayout = null;

    try {
      await startViewTransition(() => this.#setLayout(value), ['product-grid']);
    } finally {
      this.#isAnimating = false;
    }

    this.#persistLayout(value);

    // If the user picked another layout while this one was animating, apply the
    // most recent choice now so the grid always ends on what the radios show.
    if (this.#pendingLayout !== null && this.#pendingLayout !== this.#currentLayout) {
      const next = this.#pendingLayout;
      this.#pendingLayout = null;
      this.#animateLayoutChange(next);
    } else {
      this.#pendingLayout = null;
    }
  };

  /**
   * Sets the layout on the grid.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
    this.#currentLayout = value;
  }

  /**
   * Persists the chosen layout for the active viewport so it survives
   * navigation within the session.
   *
   * @param {string} value
   */
  #persistLayout(value) {
    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      try {
        sessionStorage.setItem(`product-grid-view-${viewport}`, value);
      } catch {
        // Ignore storage failures (private mode, quota) — layout still applies.
      }
    });
  }

  /**
   * Handles the media query change event. Resets the radios and grid to the
   * default layout for the new viewport so we never leave a viewport-specific
   * value (e.g. `mobile-triple`) applied on the wrong breakpoint. When the
   * grid contains interleaved promo modules, desktop falls back to the
   * four-column two-column view instead, mirroring the server-rendered
   * fallback in product-grid.liquid so the full-product-rows rule holds.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const hasModules = Boolean(this.refs.grid?.querySelector('.product-grid__item--module'));
    const targetLayout = event.matches && hasModules ? 'two-column' : 'default';

    const targetElement = event.matches
      ? this.querySelector(
          hasModules ? 'input[name="grid"][value="two-column"]' : '[data-grid-layout="desktop-default-option"]'
        )
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#pendingLayout = null;
    this.#setLayout(targetLayout);
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
